import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { useMediaQuery } from '@ifca-root/react-component/src/helpers/PDFFormatter/mediaSize';
import {
  useGetCompanyQuery,
  useGetCustomerQuery,
  useGetSubcontractQuery,
  useGetJournalEntryQuery,
} from 'generated/graphql';
import { extractHtmlValue } from './ELetterService';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

export const ELetterPreview = (props: any) => {
  const {
    previewDetail,
    allpreviewDetail,
    accountType,
    docType,
    submittedByLedger,
    remapData, // Remove manual page control props // page, // pageLength,
    page = 0,
    pageLength = 1,
    htmlTemplate,
    isView,
    threshold,
    isPOWithRepeatedHeaders = false,
    pageNumber,
    totalPages,
    approvalLog,
    watermarkData,
    approvedClaimData,
    getGlCostCentre,
    getGlMasterCOA,
    // onFooterMeasure,
  } = props; // Remove height as it's not used in the return block below

  const [width /* height */] = useMediaQuery();
  const companyID = localStorage.getItem('companyID');

  const { ID, costAdjustmentID }: any = useParams();

  const {
    loading: companyLoading,
    data: { getCompany } = { getCompany: [] },
  } = useGetCompanyQuery({
    fetchPolicy: 'network-only',
    variables: { companyID },
  });

  const shouldFetchCustomer =
    accountType !== 'project-purchase' &&
    accountType !== 'project-costing' &&
    !!ID &&
    !!companyID;

  const {
    loading: customerLoading,
    data: { getCustomer } = { getCustomer: [] },
  } = useGetCustomerQuery({
    fetchPolicy: 'network-only',
    variables: { ID, companyID },
    skip: !shouldFetchCustomer,
  });

  const shouldFetchSubcontract =
    accountType !== 'supplier-account' &&
    accountType !== 'project-costing' &&
    !!ID &&
    !!companyID;

  const {
    loading: subcontractLoading,
    data: { getSubcontract } = { getSubcontract: [] },
  } = useGetSubcontractQuery({
    fetchPolicy: 'network-only',
    variables: { companyID },
    skip: !shouldFetchSubcontract,
  }); // Use all data (assuming index 0 contains the full dataset // or that remapData is not used when generating the single long page for PDF)

  const shouldFetchJournalEntry =
    accountType === 'project-costing' &&
    !!(costAdjustmentID || ID) &&
    !!companyID;
  const {
    loading: journalEntryLoading,
    data: { getJournalEntry } = { getJournalEntry: null },
  } = useGetJournalEntryQuery({
    fetchPolicy: 'network-only',
    variables: { ID: costAdjustmentID || ID },
    skip: !shouldFetchJournalEntry,
  });

  // For POs with repeated headers, use the page-specific data
  // Otherwise, use the original logic
  const dataDetails = isPOWithRepeatedHeaders
    ? previewDetail // previewDetail already contains page-specific data
    : remapData
    ? remapData[page]
    : previewDetail;

  const [finalHTML, setFinalHTML] = React.useState('');

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [contentFooterPos, setContentFooterPos] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const el =
        (container.querySelector('#PageFooter') as HTMLElement) ||
        (container.querySelector('#ContentFooter') as HTMLElement);
      if (!el) {
        setContentFooterPos(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      const top = rect.top + window.scrollY;
      setContentFooterPos({
        top,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
      });

      const nonBreaking = container.querySelector('.mce-nbsp-wrap');
      const containerCheck = containerRef.current;

      const marker = Array.from(
        containerCheck?.querySelectorAll('strong, p') || [],
      ).find(el =>
        Array.from(el.childNodes).some(
          node =>
            node.nodeType === Node.COMMENT_NODE &&
            node.textContent?.trim() === 'pagebreak',
        ),
      );

      // Auto-insert a page break before the footer if it falls beyond threshold
      // CX: 3631: Allow each form to have its own threshold, if no threshold is parsed will use default 2917.
      // Please help test for each form, it seems fine for now
      const THRESHOLD = typeof threshold === 'number' ? threshold : 2917;
      const existingBreak = container.querySelector('.auto-page-break');
      if (top > THRESHOLD && !marker && !nonBreaking) {
        if (!existingBreak) {
          const br = document.createElement('div');
          br.className = 'auto-page-break page-break';
          br.setAttribute(
            'style',
            'page-break-after: always; break-after: page; height: 0; margin: 0; padding: 0;',
          );
          br.setAttribute('data-auto-break', 'true');
          // Insert the break immediately before the footer element
          if (el.parentNode) el.parentNode.insertBefore(br, el);
          // Re-measure after insertion to update positions
          requestAnimationFrame(measure);
        }
      } else if (existingBreak && !marker && !nonBreaking) {
        existingBreak.remove();
      } else if (marker && !nonBreaking) {
        const existingPageBreak = container.querySelector('.page-break');

        if (existingPageBreak) {
          existingPageBreak?.remove();
        }

        const breakEl = document.createElement('div');
        breakEl.className = 'page-break';
        breakEl.style.pageBreakAfter = 'always';
        breakEl.style.breakAfter = 'page';
        breakEl.style.height = '0';

        marker.parentNode?.insertBefore(breakEl, marker);
      } else if (nonBreaking) {
        existingBreak.remove();
      }
    };

    measure();

    let ro: ResizeObserver | null = null;
    if ((window as any).ResizeObserver) {
      ro = new (window as any).ResizeObserver(measure);
      const footerEl =
        (container.querySelector('#PageFooter') as Element) ||
        (container.querySelector('#ContentFooter') as Element);
      if (footerEl) ro.observe(footerEl);
    }

    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [finalHTML, width]);

  // useEffect(() => {
  //   if (contentFooterPos) {
  //     // Might need this later on. Dont remove yet.
  //     // Call parent callback if provided
  //     // onFooterMeasure(contentFooterPos);
  //   }
  // }, [contentFooterPos]);

  const ReturnHTML = () => {
    let htmlContent;

    // Prepare data with page information for the template
    const templateData = {
      ...dataDetails,
      // Ensure page number and total pages are available
      PageNo: pageNumber || page + 1,
      TotalPage: totalPages || pageLength,
    };

    htmlContent = extractHtmlValue(
      htmlTemplate,
      accountType,
      docType,
      previewDetail,
      allpreviewDetail,
      getCompany,
      getJournalEntry,
      getCustomer,
      getSubcontract, // Pass placeholder/remove page and pageLength params // if extractHtmlValue is now updated to return all content.
      isPOWithRepeatedHeaders ? 0 : page, // For POs with repeated headers, always use page 0
      totalPages || pageLength,
      submittedByLedger,
      threshold,
      approvalLog,
      watermarkData,
      approvedClaimData,
      getGlCostCentre,
      getGlMasterCOA,
    );

    let parser = new DOMParser();
    let doc = parser.parseFromString(
      // Remove the fixed height CSS that locks it to one page.
      htmlContent.replace('height: 297mm', ''),
      'text/html',
    ); // START: CRITICAL CSS INJECTION FOR HORIZONTAL FIT // This forces tables inside the HTML to be responsive and prevent overflow

    const style = doc.createElement('style');
    const isPurchaseOrder =
      docType.includes('purchase-order') ||
      docType.includes('direct-po') ||
      docType.includes('direct-po-on-behalf') ||
      docType.includes('po-on-behalf');

    const currentPage = pageNumber || page + 1;
    const totalPagesValue = totalPages || pageLength;
    const isLastPage = currentPage === totalPagesValue;
    if (!isLastPage) {
      const contentFooter = doc.querySelector('#ContentFooter');
      const itemFooter = doc.querySelector('#ItemFooter');

      if (contentFooter) contentFooter.remove();
      if (itemFooter) itemFooter.remove();

      if (!doc.querySelector('.page-continuation')) {
        const continuationMsg = doc.createElement('div');
        continuationMsg.className = 'page-continuation';
        continuationMsg.textContent = '*Continued on the next page*';
        continuationMsg.setAttribute(
          'style',
          'text-align:center; font-style:italic; color:#666; margin:12px 0;',
        );

        const body = doc.querySelector('body') || doc.documentElement;

        const firstFooter =
          doc.querySelector('#ContentFooter') ||
          doc.querySelector('#ItemFooter');
        if (firstFooter && firstFooter.parentNode) {
          firstFooter.parentNode.insertBefore(continuationMsg, firstFooter);
        } else {
          body.appendChild(continuationMsg);
        }
      }
    }

    const shouldHideFooter = isPurchaseOrder && !isLastPage;

    if (shouldHideFooter) {
      style.textContent = `
        #ContentFooter,
        #ItemFooter {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          
        }
      `;
    } else {
      style.textContent = `
        #ContentFooter,
        #ItemFooter {
          display: block !important;
          visibility: visible !important;
          height: auto !important;
        }
      `;
    }

    let existingStyle = style.textContent || '';
    style.textContent =
      existingStyle +
      `
 /* PREVIEW CSS - Maintain original fixed width */
 
 /* Push ItemFooter to bottom of page on print */
 @media print {
   #ItemFooter {
     margin-top: auto !important;
     page-break-inside: avoid !important;
     position: running(footer) !important;
   }
   
   @page {
     @bottom-center {
       content: element(footer);
     }
   }
 }
 
 /* Keep original table styling */
 table {
   table-layout: auto !important;
   width: 100% !important;
 }

 /* Ensure text wrapping but keep reasonable padding */
 td, th {
   word-break: break-word !important;
   overflow-wrap: break-word !important;
   padding: 6px 4px !important;
 }

 /* Relax the column constraints for preview */
 td:nth-child(3), th:nth-child(3) {
   max-width: none !important;
   min-width: 150px !important;
 }

 /* CRITICAL: Force the original 850px fixed width */
 #pdf-data-print {
   width: 850px !important; /* Force the original template width */
   max-width: 850px !important;
   min-width: 850px !important;
   overflow: visible !important;
   margin: 0 auto !important; /* Center it */
 }
    #pdf-data-print > div {
   max-width: none !important; /* Remove A4 constraint */
   width: auto !important; /* Natural width */
 /* PDF-SPECIFIC STYLES FOR HEADER REPETITION */
    @media print {
      /* Define running elements for header and footer */
      .header {
        position: running(header);
      }
      
      .footer {
        position: running(footer);
      }

      /* Page margins with space for headers and footers */
      @page {
        margin-top: 100px;
        margin-bottom: 50px;
        
        /* Insert running headers and footers */
        @top-center {
          content: element(header);
        }
        
        @bottom-center {
          content: element(footer);
        }
      }

      /* Ensure page breaks work correctly */
      .page-break {
        page-break-after: always;
        break-after: page;
      }

       /* Make sure each PO page starts on a new page */
       [data-is-po="true"] {
    page-break-before: always;
  }
}

      /* Reset body margins for print */
      body {
        margin: 0;
        padding: 0;
      }

      /* Ensure the main content doesn't overlap with headers/footers */
      #pdf-data-print {
        margin-top: 0 !important;
      }
    }

    /* Alternative approach for browsers that don't support running() */
    .repeat-header {
      display: none;
    }

    @media print {
      .repeat-header {
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        background: white;
        z-index: 9999;
      }
      
      /* Add top margin to content to avoid overlap */
      body {
        margin-top: 100px;
      }
    }
  `;
    const head = doc.querySelector('head');
    if (head) {
      head.appendChild(style);
    }

    // If your HTML template has a header section, add running class to it
    const headerElements = doc.querySelectorAll('.header, thead, .page-header');
    headerElements.forEach(header => {
      header.classList.add('header');
    });

    // If you have footer elements
    const footerElements = doc.querySelectorAll('.footer, tfoot, .page-footer');
    footerElements.forEach(footer => {
      footer.classList.add('footer');
    });

    htmlContent = doc.documentElement.outerHTML;

    return htmlContent;
  };

  React.useEffect(() => {
    const loadHTML = async () => {
      const html = await ReturnHTML();
      setFinalHTML(html);
    };

    if (
      !companyLoading &&
      !customerLoading &&
      !subcontractLoading &&
      !journalEntryLoading
    ) {
      loadHTML();
    }
  }, [
    companyLoading,
    customerLoading,
    subcontractLoading,
    journalEntryLoading,
    dataDetails,
    previewDetail,
    allpreviewDetail,
    htmlTemplate,
    getCompany,
    page,
    pageLength,
    getJournalEntry,
    isPOWithRepeatedHeaders,
    pageNumber,
    totalPages,
    approvalLog,
    getGlCostCentre,
    getGlMasterCOA,
  ]);

  const isPurchaseOrderType =
    docType.includes('purchase-order') ||
    docType.includes('direct-po') ||
    docType.includes('direct-po-on-behalf') ||
    docType.includes('po-on-behalf');

  const currentPageNo = pageNumber || page + 1;
  const totalPageCount = totalPages || pageLength;
  const isLastPage = currentPageNo === totalPageCount;

  return (
    <>
      {companyLoading && <Loading />}
      {customerLoading && <Loading />}
      {subcontractLoading && <Loading />}
      {journalEntryLoading && <Loading />}
      <div
        ref={containerRef}
        id={`pdf-data-print-${docType}`}
        data-claim={docType}
        data-is-po={isPurchaseOrderType ? 'true' : 'false'}
        data-page={pageNumber || page + 1}
        data-total-pages={totalPages || pageLength}
        style={
          width < 769 && isView === true
            ? {
                width: `210mm`,
                height: 'auto',
                minHeight: '297mm',
                transform: `scale(${Math.min(width / 800, 0.9)})`,
                transformOrigin: 'top left',
                overflow: 'visible',
              }
            : width < 769 && isPurchaseOrderType && isView
            ? {
                width: `210mm`,
                height: 'auto',
                minHeight: '297mm',
                transform: `scale(${Math.min(width / 800, 0.9)})`,
                transformOrigin: 'top left',
                overflow: 'visible',
              }
            : {}
        }
        dangerouslySetInnerHTML={{ __html: finalHTML }}
      />{' '}
    </>
  );
};

export default ELetterPreview;
